// Newsdesk interface language.
//
// The interface language is a property setting (Settings → Language). The
// English wording in the code IS the translation key: tr("Save changes")
// looks that sentence up in src/locales/es.js and falls back to the English
// when no translation exists, so a missed string shows English rather than
// a blank or a key name. frontend/scripts/i18nCoverage.test.mjs fails when a
// tr() string has no Spanish entry.
//
//   tr("Saved")                        plain text
//   tr("Found {n} articles", { n })    with values
//
// tr() is a plain function, not a hook, so it also works in toasts, click
// handlers and pure helpers. AuthGate remounts the app when the language
// changes, which re-reads everything. Module-level tables must not call
// tr() while loading, because the language isn't known yet; they use a
// getter instead:  { value: "x", get label() { return tr("Upload"); } }
//
// This module stays free of React so node tests can import it.

import es from "../locales/es.js";

export const DEFAULT_LANGUAGE = "en";
export const SUPPORTED_LANGUAGES = ["en", "es"];

const CATALOGS = { es };
const LOCALES = { en: "en-US", es: "es-MX" };

let current = DEFAULT_LANGUAGE;

export function normalizeLanguage(value) {
  return SUPPORTED_LANGUAGES.includes(value) ? value : DEFAULT_LANGUAGE;
}

export function setLanguage(language) {
  current = normalizeLanguage(language);
  if (typeof document !== "undefined") {
    document.documentElement.lang = current;
  }
  return current;
}

export function getLanguage() {
  return current;
}

// Before anyone signs in there's no property, so the login screen follows
// the browser. Passed in so this stays testable.
export function browserLanguage(navigatorLanguage) {
  return String(navigatorLanguage || "").toLowerCase().startsWith("es")
    ? "es"
    : DEFAULT_LANGUAGE;
}

// The locale for dates and numbers. English keeps whatever the call site
// used before (often undefined = the browser's locale), so nothing about
// existing English properties changes.
export function uiLocale(englishLocale) {
  return current === DEFAULT_LANGUAGE ? englishLocale : LOCALES[current];
}

export function fullLocale() {
  return LOCALES[current];
}

function isPlainValue(v) {
  return v === null || v === undefined || typeof v !== "object";
}

function plainText(v) {
  return v === null || v === undefined || v === false || v === true
    ? ""
    : String(v);
}

// Splits "a {x} b" into ["a ", {key:"x"}, " b"]. Only names present in
// `vars` are placeholders, so literal tokens such as {source_name} in help
// text survive untouched.
function splitPlaceholders(text, vars) {
  const parts = [];
  const re = /\{([A-Za-z_][A-Za-z0-9_]*)\}/g;
  let last = 0;
  let m;
  while ((m = re.exec(text))) {
    if (!Object.prototype.hasOwnProperty.call(vars, m[1])) continue;
    if (m.index > last) parts.push(text.slice(last, m.index));
    parts.push({ key: m[1] });
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}

export function translate(english) {
  const catalog = CATALOGS[current];
  if (!catalog) return english;
  const found = catalog[english];
  return typeof found === "string" ? found : english;
}

export function tr(english, vars) {
  const text = translate(english);
  if (!vars) return text;
  const parts = splitPlaceholders(text, vars);
  // A value can be a React element ({renderBadge()}); return a list then,
  // which React renders in order.
  if (parts.some((p) => typeof p !== "string" && !isPlainValue(vars[p.key]))) {
    return parts.map((p) => (typeof p === "string" ? p : vars[p.key]));
  }
  return parts
    .map((p) => (typeof p === "string" ? p : plainText(vars[p.key])))
    .join("");
}

// A module-level table that needs translated text but can't use getters,
// such as a list of [value, label] pairs. It's rebuilt on every read, so
// it always follows the current language.
export function lazyList(build) {
  return new Proxy([], {
    get(_target, prop) {
      const list = build();
      const value = list[prop];
      return typeof value === "function" ? value.bind(list) : value;
    },
    has(_target, prop) {
      return prop in build();
    },
  });
}

const ES_MONTHS = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];
const ES_WEEKDAYS = ["dom", "lun", "mar", "mié", "jue", "vie", "sáb"];

// "3 oct 2026", or "lun, 5 oct 2026" with a weekday (0 = Sunday). For the
// dates that are built by hand so no timezone can shift them.
export function spanishShortDate(year, monthIndex, day, weekday) {
  const date = `${day} ${ES_MONTHS[monthIndex]} ${year}`;
  return weekday === undefined ? date : `${ES_WEEKDAYS[weekday]}, ${date}`;
}

// Splits "Use <0>{name}</0> now" into text and numbered element nodes.
const TOKEN = /<(\/?)(\d+)(\/?)>/g;

export function parseRich(text) {
  const root = { children: [] };
  const stack = [root];
  let last = 0;
  let m;
  TOKEN.lastIndex = 0;
  while ((m = TOKEN.exec(text))) {
    const top = stack[stack.length - 1];
    if (m.index > last) top.children.push(text.slice(last, m.index));
    last = m.index + m[0].length;
    const index = Number(m[2]);
    if (m[1]) {
      if (stack.length > 1) stack.pop();
    } else {
      const node = { index, children: [] };
      top.children.push(node);
      if (!m[3]) stack.push(node);
    }
  }
  if (last < text.length) root.children.push(text.slice(last));
  return root.children;
}
